import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Drawer as MuiDrawer,
  Box,
  Divider,
  List,
  ListItemButton,
  IconButton,
  useTheme,
  Typography,
  ListItemText,
} from "@mui/material";
import { Menu, Language, Call, Diversity3, InfoOutlined } from "@mui/icons-material";
import EggIcon from '@mui/icons-material/Egg';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useTranslation } from "react-i18next";
import HomeIcon from "../../icons/header/HomeIcon";
import PromotionIcon from "../../icons/header/PromotionIcon";
import VipIcon from "../../icons/header/VipIcon";
import Image from "./Image";
import UserCard from "./UserCard";
import * as Constant from "../../features/constant";
import {
  useGetGameCategoryQuery,
} from "../../features/api/gameApiSlice";
import { selectCurrentToken } from "../../features/slices/authSlice";

const useImageWithFallback = (url) => {
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (!url) return;
    const img = new window.Image();
    img.src = url;
    img.onload = () => setIsValid(true);
    img.onerror = () => setIsValid(false);
  }, [url]);

  return isValid;
};

const drawerWidth = 220;

const Drawer = ({ user, window, isBD }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const container =
    window !== undefined ? () => window().document.body : undefined;
  const [drawerOpen, setDrawerOpen] = useState(true); // Set initial state to true
  const isLoggedIn = useSelector(selectCurrentToken);

  // Load image URLs from env
  const promotionImgUrl = process.env.REACT_APP_COLOR_DRAWER_PROMOTIONICON_IMAGE;
  const vipImgUrl = process.env.REACT_APP_COLOR_DRAWER_VIPICON_IMAGE;

  // Check if they are valid
  const promotionImgValid = useImageWithFallback(promotionImgUrl);
  const vipImgValid = useImageWithFallback(vipImgUrl);

  const showReferral = process.env.REACT_APP_SHOW_REFERRAL === 'true';

  const drawerItems = [
    {
      label: t("drawer.home"),
      code: "home",
      icon: (
        <HomeIcon width={25} height={25} color={process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main} />
      ),
    },
    ...(isLoggedIn && process.env.REACT_APP_GOLDEN_EGG ? [
      {
        label: "Golden Egg",
        code: "goldenegg",
        icon: (
          <EggIcon sx={{ color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR, height: 25, width: 25 }} />
        ),
      },
    ] : []),

    ...(isLoggedIn && process.env.REACT_APP_SPINWHEEL ? [
      {
        label: "Spinwheel",
        code: "SpinWheel",
        icon: (
          <EmojiEventsIcon sx={{ color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR, height: 25, width: 25 }} />
        ),
      },
    ] : []),

    ...(showReferral
      ? [
        {
          label: t("footer.affiliate"),
          code: "affiliate",
          icon: (
            <Diversity3
              sx={{
                color:
                  process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR ||
                  theme.palette.primary.main,
              }}
            />
          ),
        },
      ]
      : []),
    {
      label: t("drawer.promotion"),
      code: "promotion",
      icon: promotionImgValid ? (
        <img
          src={promotionImgUrl}
          alt="promotion"
          width={25}
          height={25}
          style={{ objectFit: "contain" }}
        />
      ) : (
        <PromotionIcon
          width={25}
          height={25}
          color={
            process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR ||
            theme.palette.primary.main
          }
        />
      ),
    },
    {
      label: t("drawer.vip"),
      code: "vip",
      icon: vipImgValid ? (
        <img
          src={vipImgUrl}
          alt="vip"
          width={25}
          height={25}
          style={{ objectFit: "contain" }}
        />
      ) : (
        <VipIcon
          width={25}
          height={25}
          color={
            process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR ||
            theme.palette.primary.main
          }
        />
      ),
    },
  ];

  const {
    data: gameCategory,
    isFetching: isGameCategoryFetching,
    isLoading: isGameCategoryLoading,
    isError: isGameCategoryError,
    isSuccess: isGameCategorySuccess,
    error: gameCategoryError,
  } = useGetGameCategoryQuery({ prefetch: true });

  Constant.getCustomMenuItems(gameCategory?.result, t).map(item => {
    drawerItems.push({
      isGameCategory: true,
      label: t('gamecategory.' + item.code.toLowerCase().replaceAll("_", "").replaceAll("-", "")),
      code: item.code.replaceAll("_", "-"),
      icon: (
        <img src={item.imageLogoOnly} alt={item.name} style={{ width: '25px', height: '25px' }} />
      ),
    });
  });

  if (process.env.REACT_APP_SHOW_REFERRAL === 'true') {
    drawerItems.push({
      label: t("drawer.referandearn"),
      code: "referral",
      icon: (
        <Diversity3
          sx={{
            color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main,
          }}
        />
      ),
    });
  }
  drawerItems.push({
    label: t("drawer.contactus"),
    code: "contactus",
    icon: (
      <Call
        sx={{
          color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main,
        }}
      />
    ),
  });
  drawerItems.push({
    label: t("drawer.infocenter"),
    code: "infocenter",
    icon: (
      <InfoOutlined
        sx={{
          color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main,
        }}
      />
    ),
  });
  drawerItems.push({
    label: t("drawer.language"),
    code: "language",
    icon: (
      <Language
        sx={{
          color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main,
        }}
      />
    ),
  });
  // {
  //   label: "Affiliate",
  //   icon: <Diversity3 sx={{ display: "block", width: "100%", mb: 1 }} />,
  // },
  // ];

  const handleDrawerToggle = (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(!drawerOpen);
    console.log("Drawer state:", drawerOpen);

  };

  // const handleGameDrawerToggle = () => {
  //   setGameDrawerOpen(!gameDrawerOpen);
  // };

  const drawerContent = (
    <>
      <Box>
        <UserCard
          isDrawer={true}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          user={user}
        />
      </Box>
      <Divider
        sx={{ bgcolor: theme.palette.primary.main, height: "5px" }}
      />
      <List
        sx={{
          justifyContent: "center",
          alignSelf: "center",
          px: 3,
          paddingLeft: "0px"
        }}
      >
        {drawerItems.map((item, index) => (
          <ListItemButton
            key={index}
            onClick={(event) => {
              if (item.code === 'affiliate' && process.env.REACT_APP_AFFILIATE_SITE) {
                const a = document.createElement('a');
                a.href = process.env.REACT_APP_AFFILIATE_SITE;
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              } else {
                navigate(
                  item?.code?.toLowerCase() === "home"
                    ? Constant.getPath("/")
                    : Constant.getPath("/" + item?.code?.toLowerCase().replace(/\s+/g, ""))
                );
                if (item.isGameCategory) {
                  navigate(0);
                }
              }
              handleDrawerToggle(event);
            }}
            sx={{
              width: "100%",
              mb: 0,
            }}
            disableRipple
          >
            <>
              {item.icon}
              <ListItemText
                sx={{
                  ml: 1,
                  fontWeight: 600,
                  lineHeight: 0,
                  fontSize: "14px",
                  color: "white",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                }}
                disableTypography
              >
                {item.label}
              </ListItemText>
            </>
          </ListItemButton>
        ))}
      </List>
    </>
  );

  return (
    <>
      <MuiDrawer
        container={container}
        variant="temporary"
        open={drawerOpen}  // Change here
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            bgcolor: theme.palette.background.paper,
          },
        }}
      >
        {drawerContent}
      </MuiDrawer>
    </>
  );
};

Drawer.propTypes = {
  user: PropTypes.object,
};

Drawer.defaultProps = {
  user: {},
};

export default Drawer;
